import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Progress } from '@/components/ui/progress';
import {
  Upload, Download, Loader2, CheckCircle, AlertCircle, RotateCcw,
  FileText, Video, X, FolderOpen,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useCredits, CREDIT_OPERATIONS } from '@/hooks/useCredits';
import { useCreditConfirm } from '@/components/credits/CreditConfirmDialog';
import { UpgradeModal } from './subscription';
import { LoginModal } from './LoginModal';
import { ToolShell, StepCard, StepHeader, type ToolStep } from './tool-shell';
import LibraryPicker, { probeVideoUrl, stagedFileName, type StagedLibraryFile } from './library/LibraryPicker';
import { useNavigate } from 'react-router-dom';

type WizardStep = 'upload' | 'captions' | 'process' | 'download';

const STEPS: ToolStep[] = [
  { key: 'upload', label: 'Upload', blurb: 'Drop in the tape that needs captions.' },
  { key: 'captions', label: 'Captions', blurb: 'Auto-transcribe, then pick a caption style.' },
  { key: 'process', label: 'Render', blurb: 'Burn the captions into the export.' },
  { key: 'download', label: 'Download', blurb: 'Save the captioned tape.' },
];

const STEP_ORDER: WizardStep[] = ['upload', 'captions', 'process', 'download'];

interface VideoInfo {
  name: string;
  objectUrl: string;
  s3Key: string;
  s3Url: string;
  duration: number;
  width: number;
  height: number;
  status: 'uploading' | 'converting' | 'uploaded' | 'error';
  uploadProgress?: number;
}

const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms}`;
};

export const CaptionsTool = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getAuthHeaders, getAuthToken } = useAuth();
  const { isSubscriber, loading: subscriptionLoading } = useSubscription();
  const { checkCredits } = useCredits();
  const { confirmCredit, CreditConfirmDialog } = useCreditConfirm();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(new Set());

  // Upload state
  const [video, setVideo] = useState<VideoInfo | null>(null);

  // Caption state
  const [captionStyle, setCaptionStyle] = useState<string>('default');
  const [transcribeStatus, setTranscribeStatus] = useState<'idle' | 'transcribing' | 'completed' | 'failed'>('idle');
  const [transcriptWords, setTranscriptWords] = useState<any[]>([]);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcribeError, setTranscribeError] = useState('');

  // Render state
  const [renderStatus, setRenderStatus] = useState<'idle' | 'rendering' | 'completed' | 'failed'>('idle');
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderError, setRenderError] = useState('');

  // Download state
  const [finalVideoUrl, setFinalVideoUrl] = useState('');

  // Track paid credit operations so restarts don't re-charge
  const [creditsPaidFor, setCreditsPaidFor] = useState<Set<string>>(new Set());

  const previewVideoRef = useRef<HTMLVideoElement>(null);

  // Credit confirmation helper
  const isCreditUser = useCallback(() => !!getAuthToken(), [getAuthToken]);

  const requireCreditConfirmation = useCallback(async (
    operationType: string,
    operationName: string,
    onConfirm: () => void
  ) => {
    // Skip credit check if already paid in this session
    if (creditsPaidFor.has(operationType)) { onConfirm(); return; }
    if (!isCreditUser()) { onConfirm(); return; }
    const creditCheck = await checkCredits(operationType);
    if (creditCheck?.isFree) { onConfirm(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      confirmCredit(operationType as any, operationName, () => {
        setCreditsPaidFor(prev => new Set([...prev, operationType]));
        onConfirm();
      });
    } else {
      toast({ title: 'Not enough video tokens', description: 'Please purchase more video tokens to continue.', variant: 'destructive' });
    }
  }, [isCreditUser, checkCredits, isSubscriber, subscriptionLoading, confirmCredit, toast, creditsPaidFor]);

  // ===== NAVIGATION =====
  const stepIndex = STEP_ORDER.indexOf(currentStep);

  const resetFromProcess = () => {
    setRenderStatus('idle');
    setRenderProgress(0);
    setRenderError('');
    setFinalVideoUrl('');
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('process');
      next.delete('download');
      return next;
    });
  };

  const resetFromCaptions = () => {
    setCaptionStyle('default');
    setTranscribeStatus('idle');
    setTranscriptWords([]);
    setTranscriptText('');
    setTranscribeError('');
    resetFromProcess();
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('captions');
      next.delete('process');
      next.delete('download');
      return next;
    });
  };

  const resetAll = () => {
    if (video?.objectUrl.startsWith('blob:')) URL.revokeObjectURL(video.objectUrl);
    setVideo(null);
    resetFromCaptions();
    setCompletedSteps(new Set());
    setCurrentStep('upload');
  };

  const goToStep = (step: WizardStep) => {
    const targetIndex = STEP_ORDER.indexOf(step);
    if (targetIndex > stepIndex && !completedSteps.has(STEP_ORDER[targetIndex - 1])) return;
    if (targetIndex < stepIndex) {
      if (step === 'upload') resetFromCaptions();
      else if (step === 'captions') resetFromProcess();
    }
    setCurrentStep(step);
  };

  const advanceStep = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(STEP_ORDER[nextIndex]);
    }
  };

  // ===== OUTPUT CONFIG (single source video) =====
  const getSourceDimensions = (): { width: number; height: number } => {
    if (video?.width && video?.height) return { width: video.width, height: video.height };
    return { width: 1920, height: 1080 };
  };

  // Detect aspect ratio category from dimensions
  const getAspectRatioCategory = (): '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | 'custom' => {
    const { width, height } = getSourceDimensions();
    const ratio = width / height;
    if (Math.abs(ratio - 1) < 0.05) return '1:1';
    if (Math.abs(ratio - 16 / 9) < 0.1) return '16:9';
    if (Math.abs(ratio - 9 / 16) < 0.1) return '9:16';
    if (Math.abs(ratio - 4 / 3) < 0.1) return '4:3';
    if (Math.abs(ratio - 3 / 4) < 0.1) return '3:4';
    return 'custom';
  };

  // Get Shotstack output config that preserves source aspect ratio
  const getShotstackOutputConfig = () => {
    const ar = getAspectRatioCategory();
    const { width, height } = getSourceDimensions();
    // For standard 16:9, use Shotstack's built-in 'hd' resolution
    if (ar === '16:9') return { format: 'mp4', resolution: 'hd', fps: 30, quality: 'high' };
    // For all other aspect ratios, preserve source dimensions exactly when they
    // already fit inside a 1920×1080 box. Only scale down when source is bigger.
    let outW = width;
    let outH = height;
    const maxW = 1920;
    const maxH = 1080;
    if (outW > maxW || outH > maxH) {
      const scale = Math.min(maxW / outW, maxH / outH);
      outW = Math.round(outW * scale);
      outH = Math.round(outH * scale);
    }
    outW = outW % 2 === 0 ? outW : outW + 1;
    outH = outH % 2 === 0 ? outH : outH + 1;
    return { format: 'mp4', size: { width: outW, height: outH }, fps: 30, quality: 'high' };
  };

  // Base Shotstack JSON — single track, single video clip. The backend turns
  // captionData into burned captions on top of this.
  const buildShotstackJson = (): any => {
    const videoClip: any = {
      asset: { type: 'video', src: video?.s3Url, volume: 1 },
      start: 0,
      length: video?.duration || 1,
    };
    return { timeline: { tracks: [{ clips: [videoClip] }] }, output: getShotstackOutputConfig() };
  };

  // ===== STEP 1: UPLOAD =====
  const uploadFileToS3 = async (file: File) => {
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type || 'video/mp4', ephemeral: true }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, key, downloadUrl } = await res.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setVideo(prev => prev ? { ...prev, uploadProgress: pct } : prev);
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      return { key, downloadUrl };
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Upload failed', description: `${file.name}: ${err.message}`, variant: 'destructive' });
      return null;
    }
  };

  // MOV → MP4 conversion via AWS MediaConvert (handles HDR→SDR tone mapping)
  const convertMov = async (s3Key: string) => {
    try {
      const res = await fetch('/api/convert-mov', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key }),
      });
      if (!res.ok) throw new Error('Failed to start conversion');
      const { jobId } = await res.json();
      pollConversion(jobId);
    } catch (err: any) {
      setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
      toast({ title: 'Conversion failed', description: err.message, variant: 'destructive' });
    }
  };

  const pollConversion = async (jobId: string) => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/convert-mov/status/${jobId}`);
        if (!res.ok) throw new Error('Failed to check conversion status');
        const data = await res.json();

        if (data.status === 'completed') {
          // Load metadata from the converted MP4 (Chrome can read H.264)
          const tempVideo = document.createElement('video');
          tempVideo.preload = 'metadata';
          tempVideo.src = data.downloadUrl;
          let metadataHandled = false;
          const updateAsUploaded = (withMetadata: boolean) => {
            if (metadataHandled) return;
            metadataHandled = true;
            setVideo(prev => prev ? {
              ...prev,
              s3Key: data.outputKey,
              s3Url: data.downloadUrl,
              objectUrl: `/api/s3/hey-joe-video-rotator/${data.outputKey}`,
              ...(withMetadata ? {
                duration: tempVideo.duration,
                width: tempVideo.videoWidth,
                height: tempVideo.videoHeight,
              } : {}),
              status: 'uploaded' as const,
            } : prev);
          };
          tempVideo.onloadedmetadata = () => updateAsUploaded(true);
          tempVideo.onerror = () => updateAsUploaded(false);
          // Fallback: if neither event fires within 10s, update anyway
          setTimeout(() => updateAsUploaded(false), 10000);
        } else if (data.status === 'failed') {
          setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
          toast({ title: 'MOV conversion failed', description: data.error, variant: 'destructive' });
        } else {
          // Still processing — poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch (err: any) {
        setVideo(prev => prev ? { ...prev, status: 'error' as const } : prev);
        toast({ title: 'Conversion error', description: err.message, variant: 'destructive' });
      }
    };
    poll();
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    // Require login + subscription before allowing uploads
    if (!getAuthToken()) {
      setShowLoginModal(true);
      return;
    }
    if (!subscriptionLoading && !isSubscriber) {
      setShowUpgradeModal(true);
      return;
    }

    const file = acceptedFiles.find(f =>
      f.type.startsWith('video/') || /\.(mp4|mov|webm|mkv|m4v|qt)$/i.test(f.name)
    );
    if (!file) {
      toast({ title: 'Not a video file', description: 'Drop an MP4, MOV, WebM, or MKV.', variant: 'destructive' });
      return;
    }

    // Replacing the source invalidates everything downstream
    if (video?.objectUrl.startsWith('blob:')) URL.revokeObjectURL(video.objectUrl);
    resetFromCaptions();

    const objectUrl = URL.createObjectURL(file);
    setVideo({
      name: file.name,
      objectUrl,
      s3Key: '',
      s3Url: '',
      duration: 0,
      width: 0,
      height: 0,
      status: 'uploading',
      uploadProgress: 0,
    });

    // Read duration/width/height from the local file (works for MP4/WebM;
    // MOV metadata is re-read from the converted MP4 after MediaConvert).
    const metaVideo = document.createElement('video');
    metaVideo.preload = 'metadata';
    metaVideo.onloadedmetadata = () => {
      setVideo(prev => prev ? {
        ...prev,
        duration: metaVideo.duration,
        width: metaVideo.videoWidth,
        height: metaVideo.videoHeight,
      } : prev);
    };
    metaVideo.src = objectUrl;

    const uploaded = await uploadFileToS3(file);
    if (!uploaded) return;

    const isMp4 = file.type === 'video/mp4' || /\.mp4$/i.test(file.name);
    if (!isMp4) {
      setVideo(prev => prev ? { ...prev, s3Key: uploaded.key, s3Url: uploaded.downloadUrl, status: 'converting' as const } : prev);
      convertMov(uploaded.key);
    } else {
      setVideo(prev => prev ? { ...prev, s3Key: uploaded.key, s3Url: uploaded.downloadUrl, status: 'uploaded' as const } : prev);
    }
  }, [getAuthToken, subscriptionLoading, isSubscriber, video, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': [] },
    multiple: false,
  });

  // "Choose from your library" — a staged library file slots in exactly like a
  // completed upload (the staged key already lives in the ephemeral tool bucket).
  const openLibraryPicker = () => {
    if (!getAuthToken()) { setShowLoginModal(true); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    setShowLibraryPicker(true);
  };

  const handleLibraryPick = async (f: StagedLibraryFile) => {
    const fileName = stagedFileName(f);
    // Replacing the source invalidates everything downstream (mirrors onDrop)
    if (video?.objectUrl.startsWith('blob:')) URL.revokeObjectURL(video.objectUrl);
    resetFromCaptions();

    const base = {
      name: f.name,
      objectUrl: f.downloadUrl,
      s3Key: f.key,
      s3Url: f.downloadUrl,
      duration: 0,
      width: 0,
      height: 0,
    };
    if (!/\.mp4$/i.test(fileName)) {
      // Non-MP4 library files go through the same MediaConvert path as uploads
      setVideo({ ...base, status: 'converting' });
      convertMov(f.key);
      return;
    }
    setVideo({ ...base, status: 'uploaded' });
    try {
      const meta = await probeVideoUrl(f.downloadUrl);
      setVideo(prev => prev ? { ...prev, duration: meta.duration, width: meta.width, height: meta.height } : prev);
    } catch {
      toast({ title: 'Could not read video metadata', description: 'Try re-selecting the clip.', variant: 'destructive' });
    }
  };

  const removeVideo = () => {
    if (video?.objectUrl.startsWith('blob:')) URL.revokeObjectURL(video.objectUrl);
    setVideo(null);
    resetFromCaptions();
  };

  const videoReady = video?.status === 'uploaded' && video.duration > 0;

  // ===== STEP 2: CAPTIONS (TRANSCRIPTION) =====
  const startTranscription = async () => {
    if (!video?.s3Key) return;
    setTranscribeStatus('transcribing');
    setTranscribeError('');
    try {
      const authToken = getAuthToken();
      const res = await fetch('/api/transcribe/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({
          videoUrl: video.s3Url,
          videoId: video.s3Key,
          s3Key: video.s3Key,
          bucket: 'hey-joe-video-rotator',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || 'Failed to start transcription');
      }
      const { jobId } = await res.json();
      toast({ title: 'Transcription started', description: 'Analyzing audio...' });
      pollTranscribeStatus(jobId);
    } catch (err: any) {
      setTranscribeStatus('failed');
      setTranscribeError(err.message);
      toast({ title: 'Transcription failed', description: err.message, variant: 'destructive' });
    }
  };

  const pollTranscribeStatus = (jobId: string) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`/api/transcribe/status/${jobId}`);
        const data = await res.json();
        if (data.status === 'COMPLETED') {
          clearInterval(interval);
          const words = data.transcriptJson?.results?.items || [];
          setTranscriptWords(words);
          setTranscriptText(data.plainTranscript || data.transcript || '');
          setTranscribeStatus('completed');
          toast({ title: 'Transcription complete' });
        } else if (data.status === 'FAILED') {
          clearInterval(interval);
          setTranscribeStatus('failed');
          setTranscribeError(data.error || 'Transcription failed');
          toast({ title: 'Transcription failed', description: data.error, variant: 'destructive' });
        }
        if (attempts >= 120) {
          clearInterval(interval);
          setTranscribeStatus('failed');
          setTranscribeError('Transcription timed out');
        }
      } catch {
        if (attempts >= 120) { clearInterval(interval); setTranscribeStatus('failed'); setTranscribeError('Transcription timed out'); }
      }
    }, 3000);
  };

  // Auto-start transcription when the captions step is entered — the tool
  // IS captions, so there's nothing to toggle first.
  useEffect(() => {
    if (currentStep === 'captions' && transcribeStatus === 'idle' && videoReady) {
      startTranscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, transcribeStatus, videoReady]);

  // ===== STEP 3: RENDER =====
  const startRender = () => {
    if (!video?.s3Url || transcriptWords.length === 0) {
      toast({ title: 'Not ready to render', description: 'Finish transcription first.', variant: 'destructive' });
      return;
    }

    const runRender = async () => {
      try {
        setRenderStatus('rendering');
        setRenderProgress(5);
        setRenderError('');
        const json = buildShotstackJson();
        const renderRes = await fetch('/api/shotstack/render', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            json,
            captionData: {
              enabled: true,
              assets: [{ url: video.s3Url, words: transcriptWords }],
              style: captionStyle,
            },
          }),
        });
        if (!renderRes.ok) {
          const err = await renderRes.json().catch(() => ({}));
          throw new Error((err as any).error || (err as any).details || 'Render submission failed');
        }
        const renderData = await renderRes.json();
        toast({ title: 'Rendering video...' });
        pollRenderStatus(renderData.id);
      } catch (err: any) {
        setRenderStatus('failed');
        setRenderError(err.message);
        toast({ title: 'Processing failed', description: err.message, variant: 'destructive' });
      }
    };

    requireCreditConfirmation(CREDIT_OPERATIONS.SHOTSTACK_RENDER, 'Video Render', runRender);
  };

  const pollRenderStatus = (id: string) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`/api/shotstack/render-status/${id}`);
        const data = await res.json();
        const doneUrl = data.videoUrl || data.url;
        if (data.status === 'done' && doneUrl) {
          clearInterval(interval);
          setRenderProgress(100);
          finishRender(doneUrl);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          const errMsg = data.errorDetails?.message || data.error || data.message || 'Rendering failed.';
          console.error('[CaptionsTool] Render failed:', data);
          setRenderStatus('failed');
          setRenderError(errMsg);
          toast({ title: 'Render failed', description: errMsg, variant: 'destructive' });
        } else {
          if (data.status === 'fetching') setRenderProgress(10);
          else if (data.status === 'queued') setRenderProgress(20);
          else if (data.status === 'rendering') setRenderProgress(prev => Math.min(90, prev + 5));
          else if (data.status === 'saving') setRenderProgress(95);
        }
        if (attempts >= 360) {
          clearInterval(interval);
          setRenderStatus('failed');
          toast({ title: 'Render timed out', variant: 'destructive' });
        }
      } catch { if (attempts >= 360) { clearInterval(interval); setRenderStatus('failed'); } }
    }, 5000);
  };

  const finishRender = (url: string) => {
    setFinalVideoUrl(url);
    setRenderStatus('completed');
    setCompletedSteps(prev => new Set([...prev, 'upload', 'captions', 'process'] as WizardStep[]));
    setCurrentStep('download');
  };

  // ===== STEP 4: DOWNLOAD =====
  const getDownloadFileName = () => {
    if (!video) return 'captioned_video.mp4';
    const baseName = video.name.replace(/\.[^.]+$/, ''); // Strip original extension
    return `captioned_${baseName}.mp4`;
  };

  const handleDownload = async () => {
    const fileName = getDownloadFileName();
    const isIOS = /iPhone|iPad/i.test(navigator.userAgent);

    // iOS: use Web Share API so user gets "Save Video" option for camera roll
    if (isIOS && navigator.share) {
      try {
        // Fetch as blob and share as file — this gives "Save Video" in share sheet
        const resp = await fetch(finalVideoUrl);
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], fileName, { type: 'video/mp4' });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName });
            return;
          }
        }
      } catch {
        // Share cancelled or failed — fall through to URL-based share
      }

      // Fallback: share the URL (still gives Save Video on iOS 16+)
      try {
        await navigator.share({ url: finalVideoUrl, title: fileName });
        return;
      } catch {
        // User cancelled — fall through to direct download
      }
    }

    // Determine the fetch URL — proxy URLs get download=1 for Content-Disposition
    let fetchUrl = finalVideoUrl;
    if (finalVideoUrl.includes('/api/s3/')) {
      const separator = finalVideoUrl.includes('?') ? '&' : '?';
      fetchUrl = `${finalVideoUrl}${separator}download=1&filename=${encodeURIComponent(fileName)}`;
    }

    // Desktop / Android: fetch blob and trigger download
    try {
      const resp = await fetch(fetchUrl);
      if (!resp.ok) throw new Error(`${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    } catch {
      // Fetch failed (CORS or 404) — fall through to direct navigation
    }

    // Fallback: direct navigation
    if (finalVideoUrl.includes('/api/s3/')) {
      window.location.href = fetchUrl;
    } else {
      window.open(finalVideoUrl, '_blank');
    }
  };

  // ===== STEP UIs =====
  const renderUploadStep = () => {
    const isUploading = video?.status === 'uploading';
    const isConverting = video?.status === 'converting';
    const isReady = video?.status === 'uploaded';
    const isError = video?.status === 'error';

    return (
      <StepCard>
        {/* Drop card — single video, full width. */}
        <div
          {...getRootProps()}
          className="flex flex-col justify-between cursor-pointer transition-colors"
          style={{
            padding: '18px 20px',
            minHeight: 140,
            borderRadius: 'var(--hj-r-md)',
            border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
            background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
            gap: 14,
          }}
        >
          <input {...getInputProps()} />
          <div className="flex items-center justify-between gap-3">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-3)',
                color: 'var(--hj-ink-2)',
              }}
            >
              <Upload size={18} strokeWidth={1.8} />
            </div>
            <span className="mono-label hidden sm:inline" style={{ fontSize: 9 }}>
              ⌘U
            </span>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--hj-ink)', marginBottom: 2 }}>
              Drop or browse a clip
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.4 }}>
              MP4 · MOV · WebM · MKV — one tape, captions burned in.
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={openLibraryPicker}
          className="inline-flex items-center gap-2"
          style={{
            marginTop: 10,
            padding: '8px 12px',
            borderRadius: 'var(--hj-r-sm)',
            border: '1px solid var(--hj-hairline)',
            background: 'transparent',
            color: 'var(--hj-ink-2)',
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <FolderOpen size={14} strokeWidth={1.8} />
          Choose from your library
        </button>

        {video && (
          <div style={{ marginTop: 18 }}>
            {/* Preview tile */}
            {(isReady || isUploading) && video.objectUrl && (
              <div
                className="aspect-video overflow-hidden relative"
                style={{
                  background: 'oklch(0.08 0.005 70)',
                  borderRadius: 'var(--hj-r-md)',
                  border: '1px solid var(--hj-hairline)',
                  marginBottom: 12,
                }}
              >
                <video
                  src={video.objectUrl}
                  className="w-full h-full object-contain"
                  controls
                  preload="metadata"
                  playsInline
                />
              </div>
            )}

            {/* File row */}
            <div
              className="flex items-center"
              style={{
                gap: 14,
                padding: '10px 12px',
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-2)',
                border: '1px solid var(--hj-hairline-soft)',
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0 relative overflow-hidden"
                style={{
                  width: 76,
                  height: 44,
                  borderRadius: 4,
                  background: 'oklch(0.12 0.005 70)',
                  color: 'var(--hj-ink-4)',
                }}
              >
                <Video size={16} strokeWidth={1.5} />
                {video.duration > 0 && (
                  <span
                    className="tabular"
                    style={{
                      position: 'absolute',
                      right: 3,
                      bottom: 3,
                      padding: '1px 5px',
                      borderRadius: 2,
                      background: 'oklch(0 0 0 / 0.6)',
                      color: 'var(--hj-ink)',
                      fontFamily: 'var(--hj-font-mono)',
                      fontSize: 9,
                    }}
                  >
                    {formatTime(video.duration)}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="truncate"
                  style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 500 }}
                >
                  {video.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--hj-ink-3)', marginTop: 2 }}>
                  {isUploading
                    ? `Uploading… ${video.uploadProgress ?? 0}%`
                    : isConverting
                      ? 'Converting MOV → MP4'
                      : isError
                        ? 'Upload error'
                        : video.width && video.height
                          ? `Uploaded · ${video.width}×${video.height}`
                          : 'Uploaded'}
                </div>
                {isUploading && (
                  <Progress value={video.uploadProgress ?? 0} className="h-1 mt-1.5" />
                )}
              </div>
              {isUploading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />}
              {isConverting && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />}
              {isReady && <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-yellow-ink)' }} />}
              {isError && <AlertCircle className="w-4 h-4 text-destructive" />}
              <button
                type="button"
                onClick={removeVideo}
                aria-label="Remove clip"
                className="flex items-center justify-center"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  background: 'transparent',
                  border: 0,
                  color: 'var(--hj-ink-3)',
                  cursor: 'pointer',
                }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderCaptionsStep = () => {
    const captionStyles = [
      { k: 'default',  label: 'Bottom safe',   desc: 'White, drop shadow' },
      { k: 'outline',  label: 'Outline',       desc: 'White text, thick stroke' },
      { k: 'box',      label: 'Box',           desc: 'White text on dark fill' },
      { k: 'minimal',  label: 'Tight caps',    desc: 'Mono, two lines max' },
    ];
    return (
      <StepCard>
        <StepHeader
          kicker="Step 02 · Captions"
          title="Auto-transcribe."
          subtitle="We listen to the tape, write it down, and burn the words into the export — pick a style."
        />

        {transcribeStatus === 'idle' && (
          <button
            type="button"
            onClick={startTranscription}
            className="inline-flex items-center gap-1.5"
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
              marginBottom: 18,
            }}
          >
            <FileText className="w-3.5 h-3.5" />
            Start transcription
          </button>
        )}

        {transcribeStatus === 'transcribing' && (
          <div
            className="flex items-center gap-3"
            style={{
              padding: 14,
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
              marginBottom: 18,
            }}
          >
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
            <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>Transcribing audio…</span>
          </div>
        )}

        {transcribeStatus === 'failed' && (
          <div
            style={{
              padding: 14,
              borderRadius: 'var(--hj-r-sm)',
              border: '1px solid hsl(var(--destructive) / 0.5)',
              background: 'hsl(var(--destructive) / 0.08)',
              marginBottom: 18,
            }}
          >
            <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
              <AlertCircle className="w-4 h-4" style={{ color: 'hsl(var(--destructive))' }} />
              <p style={{ fontWeight: 600, color: 'hsl(var(--destructive))' }}>Transcription failed</p>
            </div>
            {transcribeError && (
              <p style={{ fontSize: 12.5, color: 'hsl(var(--destructive) / 0.85)', marginBottom: 12 }}>
                {transcribeError}
              </p>
            )}
            <button
              type="button"
              onClick={() => { setTranscribeStatus('idle'); setTranscribeError(''); }}
              className="inline-flex items-center"
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 12,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        )}

        {transcribeStatus === 'completed' && (
          <>
            <div className="flex items-center gap-2" style={{ marginBottom: 14 }}>
              <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />
              <span style={{ fontWeight: 600, color: 'var(--hj-ink)' }}>Captions ready</span>
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-2)',
                border: '1px solid var(--hj-hairline-soft)',
                marginBottom: 16,
              }}
            >
              <div className="mono-label" style={{ marginBottom: 6 }}>Transcript preview</div>
              <p style={{ fontSize: 13, color: 'var(--hj-ink-2)', margin: 0, lineHeight: 1.5 }}>
                {transcriptText.length > 200 ? transcriptText.slice(0, 200) + '…' : transcriptText}
              </p>
            </div>

            <div className="mono-label" style={{ marginBottom: 10 }}>Caption style</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
              {captionStyles.map((s) => {
                const active = captionStyle === s.k;
                return (
                  <button
                    key={s.k}
                    type="button"
                    onClick={() => setCaptionStyle(s.k)}
                    className="text-left transition-colors"
                    style={{
                      padding: 14,
                      borderRadius: 'var(--hj-r-sm)',
                      background: active ? 'var(--hj-surface-3)' : 'var(--hj-surface-2)',
                      border: `1px solid ${active ? 'var(--hj-ink-4)' : 'var(--hj-hairline-soft)'}`,
                      color: 'var(--hj-ink)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      minHeight: 64,
                    }}
                  >
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{s.label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--hj-ink-2)', marginTop: 2 }}>{s.desc}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </StepCard>
    );
  };

  const renderProcessStep = () => {
    const styleLabels: Record<string, string> = {
      default: 'Bottom safe', outline: 'Outline', box: 'Box', minimal: 'Tight caps',
    };
    const summaryRows: { label: string; value: string }[] = [
      { label: 'Source',        value: video?.name || '—' },
      { label: 'Duration',      value: video?.duration ? formatTime(video.duration) : '—' },
      { label: 'Caption style', value: styleLabels[captionStyle] || captionStyle },
      { label: 'Words',         value: String(transcriptWords.length) },
    ];

    return (
      <StepCard>
        <StepHeader
          kicker="Step 03 · Render"
          title="Burn it in."
          subtitle="Joe stamps every word onto the picture. Don't close the tab — it's quick."
        />

        {/* Settings summary — always visible. Mono kickers + ink-2 values. */}
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
            marginBottom: 16,
          }}
        >
          <div className="mono-label" style={{ marginBottom: 8 }}>Final tape</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {summaryRows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-3">
                <span style={{ fontSize: 12, color: 'var(--hj-ink-4)' }}>{row.label}</span>
                <span
                  className="truncate text-right"
                  style={{ fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* In-flight progress card */}
        {renderStatus === 'rendering' && (
          <div
            style={{
              padding: '20px 22px',
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
              marginBottom: 14,
            }}
          >
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="rounded-full"
                  style={{
                    width: 10,
                    height: 10,
                    background: 'var(--hj-blue)',
                    boxShadow: '0 0 12px var(--hj-blue)',
                    animation: 'pulse 1.4s ease-in-out infinite',
                  }}
                />
                <span style={{ fontSize: 14.5, color: 'var(--hj-ink)', fontWeight: 500 }}>
                  Rendering captions
                </span>
              </div>
              <span
                className="tabular"
                style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 14, color: 'var(--hj-ink-2)' }}
              >
                {Math.min(100, Math.round(renderProgress))}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 999,
                background: 'var(--hj-surface-3)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, renderProgress)}%`,
                  background: 'var(--hj-blue)',
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
            <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
              <span className="mono-label">Don't close the tab</span>
              <span className="mono-label">1 video token</span>
            </div>
          </div>
        )}

        {renderStatus === 'failed' && (
          <div
            style={{
              padding: 16,
              borderRadius: 'var(--hj-r-sm)',
              border: '1px solid hsl(var(--destructive) / 0.5)',
              background: 'hsl(var(--destructive) / 0.08)',
            }}
          >
            <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
              <AlertCircle className="w-5 h-5" style={{ color: 'hsl(var(--destructive))' }} />
              <p style={{ fontWeight: 600, color: 'hsl(var(--destructive))' }}>Render failed</p>
            </div>
            {renderError && (
              <p style={{ fontSize: 13, color: 'hsl(var(--destructive) / 0.85)', margin: 0, lineHeight: 1.4 }}>
                {renderError}
              </p>
            )}
          </div>
        )}

        {renderStatus === 'completed' && (
          <div
            className="flex items-center gap-3"
            style={{
              padding: '14px 16px',
              borderRadius: 'var(--hj-r-sm)',
              background: 'oklch(0.42 0.15 145 / 0.12)',
              border: '1px solid var(--hj-green)',
              color: 'var(--hj-ink)',
            }}
          >
            <CheckCircle className="w-5 h-5" style={{ color: 'var(--hj-green)' }} />
            <div>
              <div style={{ fontWeight: 600 }}>Render complete</div>
              <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
                Continue to download your captioned tape.
              </div>
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderDownloadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 04 · Download"
        title="Tape's ready."
        subtitle="Captions burned in — save it to your device or start a new one."
      />

      {finalVideoUrl ? (
        <>
          {/* Big preview tile */}
          <div
            className="aspect-video overflow-hidden relative"
            style={{
              background: 'oklch(0.08 0.005 70)',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
              marginBottom: 16,
            }}
          >
            <video
              ref={previewVideoRef}
              src={finalVideoUrl}
              className="w-full h-full object-contain"
              controls
              preload="auto"
              playsInline
            />
          </div>

          {/* Primary action row */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-2 transition-opacity"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <Download className="w-4 h-4" />
              Save to device
            </button>
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center justify-center gap-2"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Start over
            </button>
          </div>
        </>
      ) : (
        <div
          className="flex items-center gap-3"
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
          }}
        >
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
          <span style={{ fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
            Waiting for the final render…
          </span>
        </div>
      )}
    </StepCard>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload': return renderUploadStep();
      case 'captions': return renderCaptionsStep();
      case 'process': return renderProcessStep();
      case 'download': return renderDownloadStep();
    }
  };

  // ===== SHELL ADVANCE CONFIG =====
  const currentStepAdvance: {
    onAdvance: () => void;
    label?: string;
    disabled?: boolean;
    showAdvance?: boolean;
  } = (() => {
    switch (currentStep) {
      case 'upload':
        return {
          onAdvance: advanceStep,
          label: video?.status === 'converting' ? 'Converting…' : 'Continue to Captions',
          disabled: !videoReady,
        };
      case 'captions':
        return {
          onAdvance: advanceStep,
          label: transcribeStatus === 'transcribing' ? 'Transcribing…' : 'Continue to Render',
          disabled: transcribeStatus !== 'completed',
        };
      case 'process': {
        if (renderStatus === 'idle') {
          return { onAdvance: () => startRender(), label: 'Render video · 1 video token' };
        }
        if (renderStatus === 'completed') {
          return { onAdvance: advanceStep, label: 'Continue to Download' };
        }
        if (renderStatus === 'failed') {
          return {
            onAdvance: () => { setRenderStatus('idle'); setRenderError(''); },
            label: 'Try again',
          };
        }
        // In flight — disable Next
        return { onAdvance: () => {}, label: 'Rendering…', disabled: true };
      }
      case 'download':
        // Terminal step — workspace owns its actions (Save to Device,
        // Start Over). Hide the shell-level Next.
        return { onAdvance: () => {}, showAdvance: false };
      default:
        return { onAdvance: advanceStep };
    }
  })();

  // Shell-level Back: walk to the previous step via goToStep (which resets
  // downstream state — going back from captions resets transcription).
  const onShellBack = () => {
    if (stepIndex > 0) goToStep(STEP_ORDER[stepIndex - 1]);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--hj-bg)' }}>
      <ToolShell
        toolName="Captions"
        toolNumber={`Tool · ${String(stepIndex + 1).padStart(2, '0')} / ${String(STEPS.length).padStart(2, '0')}`}
        usesCredits
        steps={STEPS}
        currentStepKey={currentStep}
        completedKeys={completedSteps as Set<string>}
        onStepChange={(k) => goToStep(k as WizardStep)}
        onBack={onShellBack}
        onAdvance={currentStepAdvance.onAdvance}
        advanceLabel={currentStepAdvance.label}
        advanceDisabled={currentStepAdvance.disabled}
        showAdvance={currentStepAdvance.showAdvance !== false}
        onBackToStudio={() => navigate('/')}
      >
        {renderCurrentStep()}
      </ToolShell>

      <CreditConfirmDialog />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="ai_feature" />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <LibraryPicker open={showLibraryPicker} onClose={() => setShowLibraryPicker(false)} onPick={handleLibraryPick} />
    </div>
  );
};
